import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { PageEmptyState } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { CreateProposalButton } from '~/components/CreateProposalButton/CreateProposalButton';
import { useAccountingCustomers } from '~/hooks/reactQuery/queries/useAccountingCustomers';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, ServerError } from '~/infra/error-response/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { newClientText } from '~/integrations/Accounting/AccountingCustomerDropdown/AccountingCustomerDropdown.utils';
import { IntegrationMappingModal } from '~/integrations/IntegrationMappingModal/IntegrationMappingModal';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import NoDataIcon from '../../assets/NoDataIcon';
import { getAccountingDetails } from '../accounting.utils';
import { CREATE_NEW_VALUE, buildContactRows, getModalState, getNumMappedContacts, getRelevantSortedContacts } from './MapContactsModal.utils';
export const GetMapContactsModalTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            MapContactsModalTID: {
                Modal: (testid)=>testid ? "".concat(testid, "-map-contacts-modal") : '',
                EmptyState: 'map-contacts-empty-state',
                NewButton: 'map-contacts-new-btn'
            }
        }
    });
export function MapContactsModal(props) {
    const { t: accountingTexts } = useLocaleKeys('integrations.accounting');
    const { t: errorTexts } = useLocaleKeys('errors');
    const { dismissModal, onChange, contactIds, accounting } = props;
    const { contacts, isLoading: contactsLoading, error: contactsError, refresh: fetchContacts } = useContacts({
        refreshOnMount: true
    });
    const { customers: accountingCustomers, isLoading: accountingCustomersLoading, error: accountingCustomersError, refresh: refreshAccountingCustomers } = useAccountingCustomers(accounting, {
        refreshOnHookMount: true
    });
    const { api, logBI } = useStoreActions();
    const vendorId = useStore(selectors.user.businessId);
    const userId = useStore(selectors.user.id);
    const [updatedContactsById, setUpdatedContactsById] = useState({});
    const { testIdPrefix, fullName, name, biCodes } = getAccountingDetails(accounting);
    const { MapContactsModalTID } = useMemo(()=>GetMapContactsModalTID(), []);
    const accountingName = fullName;
    const anchorContacts = useMemo(()=>getRelevantSortedContacts(contacts, contactIds || [], accounting).map((contact)=>{
            var _updatedContactsById_contact_id;
            return (_updatedContactsById_contact_id = updatedContactsById[contact.id()]) !== null && _updatedContactsById_contact_id !== void 0 ? _updatedContactsById_contact_id : contact;
        }), [
        contacts,
        contactIds,
        accounting,
        updatedContactsById
    ]);
    const numOfMappedContacts = useMemo(()=>getNumMappedContacts(anchorContacts, accounting, accountingCustomers), [
        anchorContacts,
        accounting,
        accountingCustomers
    ]);
    const modalState = useMemo(()=>getModalState({
            contacts,
            contactsLoading,
            contactsError,
            accountingCustomersLoading,
            accountingCustomersError
        }), [
        contacts,
        contactsLoading,
        contactsError,
        accountingCustomersLoading,
        accountingCustomersError
    ]);
    const rows = useMemo(()=>buildContactRows({
            contacts: anchorContacts,
            accounting,
            accountingCustomers
        }), [
        anchorContacts,
        accounting,
        accountingCustomers
    ]);
    const customerOptions = useMemo(()=>[
            {
                value: CREATE_NEW_VALUE,
                label: newClientText,
                isAction: true
            },
            ...accountingCustomers.map((customer)=>({
                    value: customer.id(),
                    label: customer.formattedDisplay(),
                    subLabel: customer.emailAddress() || undefined,
                    filterText: "".concat(customer.displayName(), " ").concat(customer.customerName(), " ").concat(customer.emailAddress())
                }))
        ], [
        accountingCustomers
    ]);
    function updateContact(contact) {
        setUpdatedContactsById((current)=>_object_spread_props(_object_spread({}, current), {
                [contact.id()]: contact
            }));
    }
    function refreshData() {
        fetchContacts({
            throwOnError: false
        });
        refreshAccountingCustomers();
    }
    async function linkCustomer(contact, accountingCustomerId) {
        const link = await handleRequest(api.accounting.linkContact({
            contactId: contact.id(),
            data: {
                integratedId: accountingCustomerId,
                version: contact.version()
            },
            accounting
        }));
        if (!link.success) {
            throw link.error;
        }
        onChange && onChange();
        const updatedContact = await handleRequest(api.contact.getById(contact.id()));
        if (!updatedContact.success) {
            throw updatedContact.error;
        }
        updateContact(updatedContact.data);
    }
    async function createNewCustomer(contact) {
        await api.accounting.createContact({
            contactId: contact.id(),
            accounting
        });
        onChange && onChange();
        const updatedContact = await api.contact.getById(contact.id());
        await refreshAccountingCustomers();
        updateContact(updatedContact);
    }
    function duplicateNameInfo(error) {
        return new ErrorResponse(error).hasServerError(ServerError.DuplicateNameExists) ? accountingTexts.mapContactsDuplicateName({
            accountingName
        }) : undefined;
    }
    function onMap(param) {
        let { row, value } = param;
        const contact = anchorContacts.find((c)=>c.id() === row.id);
        if (!contact) {
            return Promise.resolve();
        }
        if (value === CREATE_NEW_VALUE) {
            logBI(biCodes.pushNewContact, {
                vendorId,
                userId
            });
            return createNewCustomer(contact);
        }
        if (value === '') {
            logBI(biCodes.unlinkContact, {
                vendorId,
                userId
            });
            return linkCustomer(contact);
        }
        logBI(biCodes.linkContact, {
            vendorId,
            userId
        });
        return linkCustomer(contact, value);
    }
    return /*#__PURE__*/ _jsx(IntegrationMappingModal, {
        testId: MapContactsModalTID.Modal(testIdPrefix),
        title: accountingTexts.mapContactsToTitle({
            accountingName
        }),
        subheader: accountingTexts.mapContactsSubheader({
            accountingName
        }),
        state: modalState.content,
        items: rows,
        anchorHeader: accountingTexts.anchorContact(),
        select: {
            header: accountingTexts.mapContactsSelectHeader({
                accountingName
            }),
            selectBi: {
                name: 'accounting_customer'
            },
            options: ()=>customerOptions,
            emptySearchText: accountingTexts.noAccountingCustomers({
                name
            }),
            placeholder: accountingTexts.customerDropdown.notMapped()
        },
        onMap: onMap,
        failureInfo: duplicateNameInfo,
        emptyState: /*#__PURE__*/ _jsx(PageEmptyState, {
            testId: MapContactsModalTID.EmptyState(),
            illustration: /*#__PURE__*/ _jsx(NoDataIcon, {}),
            message: accountingTexts.mapContactsEmptyMessage({
                accountingName
            }),
            actions: /*#__PURE__*/ _jsx(CreateProposalButton, {
                text: accountingTexts.mapContactsCreateAgreement(),
                testId: MapContactsModalTID.NewButton()
            })
        }),
        onRefresh: refreshData,
        errorMessage: errorTexts.integration.dataRetrieval(),
        summary: accountingTexts.mapContactsFooter({
            mapped: String(numOfMappedContacts),
            total: String(anchorContacts.length)
        }),
        closeBi: {
            name: 'map_contacts_modal_close'
        },
        onClose: dismissModal
    });
}
